<ng-container *vsLet="claims$ | async as claims">
  <ng-container *vsLet="(paginationConfig$ | async)?.totalItems as totalItems">
    <div *ngIf="!hideFilters" class="flex gap-2 justify-between items-center flex-wrap mt-4">
      <vs-badge
        *ngIf="filters?.warrantyId"
        [vsMenuTriggerFor]="warrantyMenu"
        [vsMenuPosition]="positions"
        class="cursor-pointer"
        theme="default"
        [config]="{ iconEnd: 'x-close' }"
        >Claims for '{{ $any(filters).warrantyId }}'</vs-badge
      >
      <ng-template #warrantyMenu>
        <vs-menu class="py-0">
          <button vs-button appearance="clear" (click)="clearWarrantyIdFilter()">
            Clear Filter
            <vs-icon name="x" slot="end"></vs-icon>
          </button>
        </vs-menu>
      </ng-template>
      <div class="ml-auto flex items-start gap-2 py-1.5 sm:py-0">
        <button
          #filtersTrigger="cdkOverlayOrigin"
          cdkOverlayOrigin
          vs-button
          appearance="outline"
          (click)="$event.preventDefault(); $event.stopImmediatePropagation(); toggleFiltersOverlay()"
        >
          <vs-icon name="sliders-04" slot="start"></vs-icon>
          Filters
          <vs-badge *ngIf="activeFilterCount > 0" class="ml-1" theme="default">{{ activeFilterCount }}</vs-badge>
        </button>

        <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="filtersTrigger"
          [cdkConnectedOverlayOpen]="filtersOverlayOpen"
          [cdkConnectedOverlayPositions]="positions"
          [cdkConnectedOverlayHasBackdrop]="true"
          cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
          (backdropClick)="closeFiltersOverlay()"
          (detach)="closeFiltersOverlay()"
        >
          <vs-card class="my-2 !rounded-md !shadow-md" [style.maxWidth]="'min(90vw, 576px)'">
            <div class="p-3 w-[min(90vw,576px)] max-h-[70vh] overflow-y-auto" (click)="$event.stopPropagation()">
              <div class="flex items-center justify-between mb-2">
                <p class="text-sm font-semibold text-themed-900">Filters</p>
                <button vs-button appearance="clear" size="sm" (click)="clearAllFilters()">Clear all</button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-[1fr_1.1fr] gap-3">
                <div class="flex flex-col gap-2">
                  <p class="text-xs font-semibold uppercase tracking-wide text-themed-500">Claim</p>
                  <vs-select
                    vsInput
                    [value]="filters?.statuses ?? []"
                    [config]="{ multiple: true, placeholder: 'All statuses' }"
                    (valueChanges)="refreshTable({ statuses: $any($event) })"
                  >
                    <li *ngFor="let status of statuses" vs-select-option [value]="status">
                      <vs-badge
                        *vsLet="getBadgeStatusColor($any(status)) as theme"
                        [theme]="theme?.color"
                        [config]="{ displayStatusIcon: true }"
                      >
                        {{ status.replace('CANCELLED', 'CANCELED') | titlecase }}
                      </vs-badge>
                    </li>
                  </vs-select>

                  <ng-container *ngIf="resolutionOptions$ | async as resolutionOptions">
                    <vs-select
                      vsInput
                      [value]="filters?.resolutions ?? []"
                      [config]="{ multiple: true, placeholder: 'Resolutions' }"
                      (valueChanges)="refreshTable({ resolutions: $any($event) })"
                    >
                      <li vs-select-option [value]="unspecifiedResolutionValue">Unspecified</li>
                      <li *ngFor="let resolution of resolutionOptions" vs-select-option [value]="resolution">
                        {{ resolution }}
                      </li>
                    </vs-select>
                  </ng-container>

                  <ng-container *ngIf="lifeCycleSteps$ | async as lifeCycleSteps">
                    <vs-select
                      *ngIf="lifeCycleSteps.length"
                      vsInput
                      [value]="filters?.claimLifecycleSteps ?? []"
                      [config]="{ multiple: true, placeholder: 'Lifecycle Steps' }"
                      (valueChanges)="refreshTable({ claimLifecycleSteps: $any($event) })"
                    >
                      <li *ngFor="let step of lifeCycleSteps" vs-select-option [value]="step">
                        {{ step | titlecase }}
                      </li>
                    </vs-select>
                  </ng-container>
                </div>

                <div class="flex flex-col gap-2">
                  <p class="text-xs font-semibold uppercase tracking-wide text-themed-500">Related</p>
                  <ws-customer-picker
                    *ngIf="!isCustomerPortal && !customerId"
                    [value]="selectedCustomer"
                    (valueChanges)="refreshTable({ customer: $event })"
                  ></ws-customer-picker>

                  <vs-select
                    vsInput
                    [value]="filters?.claimPaymentStatuses ?? []"
                    [config]="{ multiple: true, placeholder: 'Payment statuses' }"
                    (valueChanges)="refreshTable({ paymentStatuses: $any($event) })"
                  >
                    <li *ngFor="let status of paymentStatuses" vs-select-option [value]="status">
                      <vs-badge
                        *vsLet="getPaymentTheme($any(status)) as theme"
                        [theme]="theme"
                        [config]="{ displayStatusIcon: true }"
                      >
                        {{ status | titlecase }}
                      </vs-badge>
                    </li>
                  </vs-select>

                  <ws-distributor-filter
                    permissionId="clm_ViewClaim"
                    placeholder="Select a Distributor"
                    [value]="selectedDistributor"
                    (valueChanges)="refreshTable({ distributor: $event })"
                  ></ws-distributor-filter>

                  <vs-select
                    vsInput
                    [value]="filters?.claimItemStatuses ?? []"
                    [config]="{ multiple: true, placeholder: 'Claim item statuses' }"
                    (valueChanges)="refreshTable({ itemStatuses: $any($event) })"
                  >
                    <li *ngFor="let status of itemStatuses" vs-select-option [value]="status">
                      <vs-badge
                        *vsLet="getClaimItemStatusColor($any(status)) as theme"
                        [theme]="theme"
                        [config]="{ displayStatusIcon: true }"
                      >
                        {{ status | titlecase }}
                      </vs-badge>
                    </li>
                  </vs-select>
                </div>
              </div>

              <ng-container *ngIf="!isCustomerPortal && (queueEnabled$ | async) === true">
                <vs-divider class="my-3"></vs-divider>

                <p class="text-xs font-semibold uppercase tracking-wide text-themed-500 mb-2">
                  {{ queueConfig.section }}
                </p>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div class="flex flex-col gap-2">
                    <label class="flex items-center gap-2 text-sm text-themed-700">
                      <input
                        vsInput
                        type="checkbox"
                        [checked]="filters?.hasOpenHold ?? false"
                        (change)="refreshTable({ hasOpenHold: $any($event.target).checked })"
                      />
                      {{ queueConfig.openHold }}
                    </label>

                    <label class="flex items-center gap-2 text-sm text-themed-700">
                      <input
                        vsInput
                        type="checkbox"
                        [checked]="filters?.hasOpenEscalation ?? false"
                        (change)="refreshTable({ hasOpenEscalation: $any($event.target).checked })"
                      />
                      {{ queueConfig.openEscalation }}
                    </label>

                    <!-- Pinned to the bottom so each row lines up with the column next to it -->
                    <div class="flex flex-col gap-2 mt-auto">
                      <vs-select
                        vsInput
                        [value]="filters?.holdDue ?? null"
                        [config]="{ placeholder: queueConfig.holdDue.placeholder }"
                        (valueChanges)="refreshTable({ holdDue: $any($event) })"
                      >
                        <li vs-select-option [value]="null">{{ queueConfig.holdDue.any }}</li>
                        <li *ngFor="let option of queueConfig.holdDue.options" vs-select-option [value]="option.value">
                          {{ option.label }}
                        </li>
                      </vs-select>

                      <vs-select
                        vsInput
                        [value]="filters?.repairDue ?? null"
                        [config]="{ placeholder: queueConfig.repairDeadline.placeholder }"
                        (valueChanges)="refreshTable({ repairDue: $any($event) })"
                      >
                        <li vs-select-option [value]="null">{{ queueConfig.repairDeadline.any }}</li>
                        <li *ngFor="let option of queueConfig.holdDue.options" vs-select-option [value]="option.value">
                          {{ option.label }}
                        </li>
                      </vs-select>

                      <vs-select
                        vsInput
                        [value]="filters?.overrideStates ?? []"
                        [config]="{ multiple: true, placeholder: queueConfig.approval.placeholder }"
                        (valueChanges)="refreshTable({ overrideStates: $any($event) })"
                      >
                        <li *ngFor="let state of approvalStates" vs-select-option [value]="state">
                          {{ queueConfig.approval.labels[state] }}
                        </li>
                      </vs-select>
                    </div>
                  </div>

                  <div class="flex flex-col justify-end gap-2">
                    <vs-select
                      vsInput
                      [value]="filters?.minAgeDays ?? null"
                      [config]="{ placeholder: queueConfig.age.placeholder }"
                      (valueChanges)="refreshTable({ minAgeDays: $any($event) })"
                    >
                      <li vs-select-option [value]="null">{{ queueConfig.age.any }}</li>
                      <li *ngFor="let option of queueConfig.age.options" vs-select-option [value]="option.value">
                        {{ option.label }}
                      </li>
                    </vs-select>

                    <vs-select
                      vsInput
                      [value]="filters?.authorizationStates ?? []"
                      [config]="{ multiple: true, placeholder: queueConfig.authorization.placeholder }"
                      (valueChanges)="refreshTable({ authorizationStates: $any($event) })"
                    >
                      <li *ngFor="let state of authorizationStates" vs-select-option [value]="state">
                        {{ queueConfig.authorization.labels[state] }}
                      </li>
                    </vs-select>

                    <vs-select
                      vsInput
                      [value]="filters?.paymentReadiness ?? []"
                      [config]="{ multiple: true, placeholder: queueConfig.paymentReadiness.placeholder }"
                      (valueChanges)="refreshTable({ paymentReadiness: $any($event) })"
                    >
                      <li *ngFor="let readiness of paymentReadinessValues" vs-select-option [value]="readiness">
                        {{ queueConfig.paymentReadiness.labels[readiness] }}
                      </li>
                    </vs-select>

                    <vs-select
                      vsInput
                      [value]="filters?.contactReasons ?? []"
                      [config]="{ multiple: true, placeholder: queueConfig.contactReason.placeholder }"
                      (valueChanges)="refreshTable({ contactReasons: $any($event) })"
                    >
                      <li *ngFor="let reason of contactReasonValues" vs-select-option [value]="reason">
                        {{ queueConfig.contactReason.labels[reason] }}
                      </li>
                    </vs-select>
                  </div>
                </div>
              </ng-container>
            </div>
          </vs-card>
        </ng-template>

        <vs-input-container class="max-w-[212px] ml-auto">
          <vs-icon name="search-lg" slot="start"></vs-icon>
          <input
            #searchInput
            vsInput
            [value]="searchInputValue"
            placeholder="Search"
            (input)="searchInput$.next(searchInput.value)"
          />
        </vs-input-container>
      </div>
    </div>

    <div
      *ngIf="totalItems !== 0; else noItems"
      class="rounded-xl overflow-hidden mt-4"
      [class]="!hideBorder ? 'border border-gray-200 shadow-sm' : ''"
    >
      <vs-table
        *ngIf="columns$ | async as columns"
        [columns]="columns"
        [data]="claims?.items"
        (rowClicked)="navigateTo($event.id)"
        (sortChanged)="onSortChanged($event)"
      ></vs-table>

      <vs-pagination
        *ngIf="hidePagination !== true"
        class="items-end"
        [config]="paginationConfig$ | async"
        (pagination)="paginationQueryRequest$.next({ page: $event.currentPage, limit: $event.itemsPerPage })"
      ></vs-pagination>
    </div>
  </ng-container>

  <ng-template #noItems>
    <div #placeholder class="empty:hidden">
      <ng-content select="[slot='placeholder']"></ng-content>
    </div>

    <vs-card
      *ngIf="!placeholder.children.length"
      class="flex flex-col justify-center gap-2 p-6 mt-4"
      [class.!border-none]="isCustomerPortal || hideBorder"
      [class.!shadow-none]="isCustomerPortal || hideBorder"
    >
      <vs-empty-state
        class="-mt-4"
        [config]="{
          title: filters?.search
            ? 'No claim items found with that search query.'
            : filters?.statuses?.length
              ? 'No ' + ($any(filters).statuses.join(', ') | lowercase) + ' claims found.'
              : filters?.distributorId
                ? 'No claims found under that distributor.'
                : 'No claims found.',
          subtitle: filters?.search
            ? 'Adjust the filters to view claims for your organization.'
            : filters?.statuses?.length
              ? 'Once a claim has been ' +
                ($any(filters).statuses.includes(open)
                  ? 'added'
                  : 'set to ' + (statuses.length === 1 ? statuses[0] : 'one of those statuses')) +
                ', its details will appear here.'
              : filters?.distributorId
                ? 'Once a claim has been created with that distributor its details will appear here.'
                : warranty && warranty.status !== active
                  ? 'This warranty is expired an no new claims can be created under it.'
                  : 'Once a claim item has been added, its details will appear here.',
          imgsrc: 'empty-clipboard',
        }"
      >
      </vs-empty-state>
      <div
        class="self-center"
        appearance="default"
        theme="primary"
        (click)="isCustomerPortal ? navigateTo('open') : openOpenClaimDialog()"
        [vsTooltip]="
          remainingPeriod && remainingPeriod > 0 && !isPastWaitingPeriod
            ? 'Remaining ' + remainingPeriod + ' ' + period + ' of the waiting period.'
            : ''
        "
        tooltipStyle="light"
      >
        <button
          *ngIf="!isCustomerPortal || warranty?.status === active"
          vs-button
          theme="primary"
          [disabled]="isPastWaitingPeriod === false"
          (click)="isCustomerPortal ? navigateTo('open') : openOpenClaimDialog(warranty, warranty?.customer)"
        >
          <vs-icon name="file-plus-03"></vs-icon> New Claim
        </button>
      </div>
    </vs-card>
  </ng-template>
</ng-container>

<ws-open-claim-dialog #openClaimDialog (closed)="refreshTable()"></ws-open-claim-dialog>
